<script setup lang="ts">
import type { HouseAskAQuestion } from '#build/components'
import type { HouseDetails } from '~/types/house/types'
import type { PeriodDurationInquiry } from '~/types/inquiry/types'

const { house } = defineProps<{
  house: HouseDetails
  modelValue: boolean
  periods: PeriodDurationInquiry[]
}>()
const emits = defineEmits<{
  'update:model-value': [boolean]
}>()

const { booking, setPrevStep, unsetBooking } = useHouseInquiryBooking(house.id)
const { isMobile } = useBreakpoint()
const { openScheduler } = useMeetingScheduler()

const sendAMessage = ref(false)
const inquiryForm = ref<InstanceType<typeof HouseAskAQuestion> | null>(null)

const scheduleACall = () => {
  sendAMessage.value = false
  emits('update:model-value', false)
  openScheduler('product page', house)
}
const close = () => {
  sendAMessage.value = false
  unsetBooking()
  emits('update:model-value', false)
}
const goBack = () => {
  if (booking.value.currentStep > 0) {
    setPrevStep()
  } else if (sendAMessage.value) {
    sendAMessage.value = false
  } else {
    close()
  }
}
</script>

<template>
  <BaseModalV2
    :class="[
      'ask-a-question',
      {
        'first-step': !sendAMessage,
        'second-step': booking.currentStep >= 1,
      },
    ]"
    size="m"
    mobile-full-height
    :primary-button="inquiryForm?.isCalendarOpen ? '' : 'true'"
    :model-value="modelValue"
    :has-back-button="isMobile && booking.currentStep < 1"
    :has-close-button="
      !inquiryForm?.isCalendarOpen && (!isMobile || booking.currentStep >= 1)
    "
    @back="goBack"
    @close="close"
    @update:model-value="$emit('update:model-value', $event)"
  >
    <template #content>
      <div class="flex h-full flex-col gap-4">
        <!-- Hide the title if calendar is opened -->
        <h2
          v-if="!inquiryForm?.isCalendarOpen"
          class="flex items-center gap-2 text-2xl"
        >
          {{
            $t(`inquiry.title[${booking.currentStep}]`, {
              houseName: house.name,
            })
          }}
          <BaseIcon
            v-if="booking.currentStep >= 1"
            name="checkCircle"
            :size="1.5"
          />
        </h2>
        <!-- "Send a message" flow -->
        <HouseAskAQuestion
          v-if="sendAMessage"
          ref="inquiryForm"
          :house="house"
          :periods="periods"
          @close="close"
        />
        <!-- Choice selection (Send a message OR Schedule a call) -->
        <template v-else>
          <div class="my-10 flex flex-col gap-4 sm:grid sm:grid-cols-2">
            <div class="ask-a-question__choice" @click="sendAMessage = true">
              <div class="flex flex-col items-start">
                <h3>
                  <BaseIcon name="messageText" :size="2" />
                  {{ $t('inquiry.sendAMessage.cta') }}
                </h3>
                <p class="text-md">
                  {{ $t('inquiry.sendAMessage.text') }}
                </p>
              </div>
              <BaseIcon v-if="isMobile" name="chevronRight" :size="2" />
            </div>
            <div class="ask-a-question__choice" @click="scheduleACall">
              <div class="flex flex-col items-start">
                <h3>
                  <BaseIcon name="phone" :size="2" />
                  {{ $t('inquiry.scheduleACall.cta') }}
                </h3>
                <p class="text-md">
                  {{ $t('inquiry.scheduleACall.text') }}
                </p>
              </div>
              <BaseIcon v-if="isMobile" name="chevronRight" :size="2" />
            </div>
          </div>
        </template>
      </div>
    </template>

    <template v-if="!inquiryForm?.isCalendarOpen" #footer>
      <InquiryModalFooter
        :form="inquiryForm"
        :house="house"
        :show-submit-btn="sendAMessage"
        @finish="close"
      />
    </template>
  </BaseModalV2>
</template>

<style scoped>
.ask-a-question__choice h3 {
  @apply flex gap-3 text-xl sm:flex-col;
}
.ask-a-question__choice {
  @apply flex items-center sm:flex-col sm:justify-end gap-4 px-6 py-6 sm:pt-16 border border-gray-200 cursor-pointer hover:shadow-[0_0_38px_0_rgba(0,0,0,0.1)];
}
</style>
<style>
.ask-a-question.second-step .modal__footer {
  @apply !justify-end;
}
.ask-a-question.first-step .modal__footer {
  @apply !border-none;
}
.ask-a-question .modal__footer {
  @apply lg:!py-4 gap-4 sm:!h-28;
}
</style>
